import React, { memo } from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { QRCodeCanvas } from "qrcode.react";
import { escapeHtml } from "../../../helper-components/helper-function";
import { TRAY_BARCODE_RENDER_OPTIONS, trayCodeValue } from "./tray-label";

// The QR canvas is drawn at this size and scaled to the label by CSS.
const QR_CANVAS_SIZE_PX = 60;
// Big enough that the QR stays sharp at thermal print resolution.
const QR_PRINT_SIZE_PX = 256;

const barcodeUrlCache = new Map();
// Vector barcode shared by the preview and the print-dialog page, so both show
// the same bars. crispEdges snaps bars to whole device dots; "none" lets the
// bars stretch to the label (a uniform stretch keeps CODE128 ratios intact).
function barcodeDataUrl(value) {
  if (barcodeUrlCache.has(value)) return barcodeUrlCache.get(value);
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  let dataUrl = "";
  try {
    JsBarcode(svg, value || "0", TRAY_BARCODE_RENDER_OPTIONS);
    svg.setAttribute("shape-rendering", "crispEdges");
    svg.setAttribute("preserveAspectRatio", "none");
    const markup = new XMLSerializer().serializeToString(svg);
    dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  } catch {}
  barcodeUrlCache.set(value, dataUrl);
  return dataUrl;
}

/**
 * Preview of one tray label — the tray name and its code, nothing else: the
 * name above the barcode, or the QR with the name beside it. Memoised because
 * a big batch renders hundreds of these and most don't change per keystroke.
 */
export const TrayLabelFace = memo(function TrayLabelFace({ tray, codeType, widthMm, heightMm }) {
  const codeValue = trayCodeValue(tray);
  // The label is paper: black on white whatever the app theme.
  const style = { width: `${widthMm}mm`, height: `${heightMm}mm`, color: "#000" };

  if (codeType === "qr") {
    return (
      <div
        style={style}
        className="rounded-lg border border-D0D5DD shadow-sm bg-white flex items-center gap-[1mm] overflow-hidden px-[1mm] py-[1mm]"
      >
        <QRCodeCanvas
          value={codeValue}
          size={QR_CANVAS_SIZE_PX}
          className="shrink-0"
          style={{ height: "100%", width: "auto", maxWidth: "50%" }}
        />
        <div className="min-w-0 text-[12px] fw_700 leading-tight break-words">
          {tray?.tray_name}
        </div>
      </div>
    );
  }

  return (
    <div
      style={style}
      className="rounded-lg border border-D0D5DD shadow-sm bg-white flex flex-col overflow-hidden px-[1mm] py-[1mm]"
    >
      <div className="text-[12px] fw_700 leading-tight truncate text-center">
        {tray?.tray_name}
      </div>
      <div className="flex items-center justify-center flex-1 min-h-0 overflow-hidden mt-[0.5mm]">
        <img
          src={barcodeDataUrl(codeValue)}
          alt=""
          style={{ height: "100%", width: "auto", maxWidth: "100%" }}
        />
      </div>
    </div>
  );
});

const qrUrlCache = new Map();
async function qrDataUrl(value) {
  if (qrUrlCache.has(value)) return qrUrlCache.get(value);
  // A 2-module margin is the quiet zone, as on the TSPL labels.
  const dataUrl = await QRCode.toDataURL(value || "0", { margin: 2, width: QR_PRINT_SIZE_PX });
  qrUrlCache.set(value, dataUrl);
  return dataUrl;
}

async function labelHtml(tray, codeType) {
  const codeValue = trayCodeValue(tray);
  const name = `<div class="name">${escapeHtml(tray?.tray_name)}</div>`;
  if (codeType === "qr") {
    return `<div class="label label-qr"><img class="qr" src="${await qrDataUrl(codeValue)}" />${name}</div>`;
  }
  return `<div class="label label-barcode">${name}<div class="code"><img src="${barcodeDataUrl(
    codeValue
  )}" /></div></div>`;
}

/**
 * The print-dialog fallback (no thermal printer picked): one page per label,
 * each sized exactly to the label plus the gap, so the driver feeds one label
 * per page. No border is printed — only the name and the code.
 * @param queue trays in print order, one entry per label
 * @param size { width, height, gap } in mm
 */
export async function trayLabelsPrintHtml(queue, codeType, { width, height, gap }) {
  const labels = await Promise.all(queue.map((tray) => labelHtml(tray, codeType)));
  const pageHeight = height + gap;
  return `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>
    @page { size: ${width}mm ${pageHeight}mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; color: #000; }
    .page { width: ${width}mm; height: ${pageHeight}mm; overflow: hidden; page-break-after: always; break-after: page; }
    .page:last-child { page-break-after: auto; break-after: auto; }
    .label { width: ${width}mm; height: ${height}mm; overflow: hidden; background: #fff; padding: 1mm; }
    .label .name { font-size: 12px; font-weight: 700; line-height: 1.15; }
    .label-barcode { display: flex; flex-direction: column; }
    .label-barcode .name { text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .label-barcode .code { flex: 1; min-height: 0; margin-top: 0.5mm; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .label-barcode .code img { height: 100%; width: auto; max-width: 100%; }
    .label-qr { display: flex; align-items: center; gap: 1mm; }
    .label-qr .qr { height: 100%; width: auto; max-width: 50%; flex-shrink: 0; image-rendering: pixelated; }
    .label-qr .name { min-width: 0; overflow-wrap: anywhere; }
  </style></head><body>${labels.map((label) => `<div class="page">${label}</div>`).join("")}</body></html>`;
}
